import cn from 'classnames';
import { CodeInput, getSegmentCssWidth } from 'rci';
import { useRef } from 'react';
import { useIsFocused } from 'use-is-focused';
import { useMatchMedia } from '#hooks/useMatchMedia.ts';
import { useMergedRefs } from '#util/mergeRefs.ts';
import styles from './MFAInput.module.css';

const SIZE = 6;
const ALLOWED_KEYS = [
  'ArrowLeft',
  'ArrowRight',
  'Backspace',
  'Delete',
  'End',
  'Enter',
  'Escape',
  'Home',
  'Meta',
  'PageDown',
  'PageUp',
];
// This breakpoint is also set in the css module
const BREAKPOINT = 500;
const SIZES = {
  small: {
    fontSize: '24px',
    padding: '8px',
    spacing: '8px',
  },
  large: {
    fontSize: '40px',
    padding: '20px',
    spacing: '0px',
  },
};

type CodeState = 'input' | 'loading' | 'error' | 'success';
type Props = React.ComponentProps<'input'> & {
  state: CodeState;
  ref?: React.Ref<HTMLInputElement>;
};

export function MFAInput({ className, ref, state, onChange, ...props }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergedRefs(ref, inputRef);
  const focused = useIsFocused(mergedRef);

  const isNarrow = useMatchMedia(`(max-width: ${BREAKPOINT}px)`);
  const size = isNarrow ? 'small' : 'large';
  const { padding, fontSize, spacing } = SIZES[size];
  const width = getSegmentCssWidth(padding);

  return (
    <CodeInput
      className={cn(
        styles.MFAInput,
        styles[`MFAInput___${state}`],
        {
          [styles.MFAInput___focused]: focused,
        },
        className
      )}
      /* eslint-disable-next-line jsx-a11y/no-autofocus */
      autoFocus={true}
      length={SIZE}
      fontFamily="inherit"
      readOnly={state !== 'input'}
      disabled={state === 'loading'}
      inputRef={mergedRef}
      padding={padding}
      spacing={spacing}
      fontSize={fontSize}
      spellCheck={false}
      inputMode="numeric"
      autoComplete="off"
      onChange={(e) => {
        // only accept numbers
        const { currentTarget: input } = e;
        input.value = input.value.replace(/\D+/g, '');
        onChange?.(e);
      }}
      onKeyDown={(e) => {
        // Prevent non-digit characters from being entered.
        if (ALLOWED_KEYS.includes(e.key)) return;

        // If a non-digit other than an allowed key is pressed, do nothing
        if (!(e.metaKey || e.ctrlKey || e.altKey) && !/^[0-9]$/i.test(e.key)) {
          e.preventDefault();
          return;
        }
      }}
      renderSegment={({ state: segmentState, index }) => {
        return (
          <div
            key={index}
            className={styles.MFAInput_segment}
            data-state={segmentState}
            style={{ width, height: '100%' }}
          >
            <div />
          </div>
        );
      }}
      {...props}
    />
  );
}
